import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { sortByLabel, sortByName, sortByTitle } from "@/lib/sortRows";
import AppLayout from "@/components/AppLayout";
import DataTable from "@/components/DataTable";
import TableSearch, { matchesQuery } from "@/components/TableSearch";
import { StatusPill, StatusSelectPill, STATUS_STYLES } from "@/components/StatusBadge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import ReportIssueModal, { ISSUE_TYPES, ISSUE_TYPE_LABELS, ISSUE_STATUSES, ISSUE_TEXT_MAX_LENGTH } from "@/components/ReportIssueModal";
import { PlusIcon } from "@phosphor-icons/react";
import { toast } from "sonner";

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString() : "—");
// timestamptz/date -> yyyy-mm-dd for a plain <input type="date">
const toDateInput = (v) => {
  if (!v) return "";
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? "" : d.toISOString().slice(0, 10);
};

const COL_STORAGE_KEY = "issues.visibleColumns";

// The segregation the Issues page opens on: which level an issue was
// reported at. "level" here always matches entityChain(iss).level.toLowerCase().
// Labelled "Reported on a Project/Task" rather than a bare "Projects"/"Tasks"
// — this counts ISSUES reported at that level (matches what clicking the tile
// filters the table to), not the number of distinct projects/tasks involved.
// A bare "Projects" read as "7 distinct projects" when it actually meant "7
// issues reported at project level", which was confusing when every one of
// those issues happened to be on the same single project.
const LEVELS = [
  { id: "all", label: "All" },
  { id: "project", label: "On a Project" },
  { id: "task", label: "On a Task" },
];

const COLUMN_DEFS = [
  { key: "number",      label: "Issue #",      cls: "px-3", weight: 108,
    sortValue: (i) => i.issue_number || "" },
  { key: "description",  label: "Description",  locked: true, cls: "px-4",
    sortValue: (i) => i.description || "" },
  { key: "entity",       label: "Reported On",  cls: "px-3", weight: 200 },
  { key: "type",         label: "Type",         cls: "px-3", weight: 128 },
  { key: "status",       label: "Status",       cls: "px-3", weight: 116 },
  { key: "raised_by",    label: "Raised By",    cls: "px-3", weight: 130 },
  { key: "assigned_to",  label: "Assigned",     cls: "px-3", weight: 130 },
  { key: "reported_date", label: "Reported",    cls: "px-3", weight: 110,
    sortValue: (i) => i.reported_date || "" },
  { key: "eta",          label: "ETA",          cls: "px-3", weight: 104,
    sortValue: (i) => i.eta || "" },
  { key: "root_cause",   label: "Root Cause",   cls: "px-4", defaultHidden: true },
  { key: "comment", label: "Comment", cls: "px-4", defaultHidden: true },
];

export default function IssuesPage() {
  const { user: currentUser } = useAuth();
  const [issues, setIssues] = useState([]);
  const [initiatives, setInitiatives] = useState([]);
  const [projects, setProjects] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [teamUsers, setTeamUsers] = useState([]);
  const [level, setLevel] = useState("all");
  const [projectFilter, setProjectFilter] = useState("all");
  const [status, setStatus] = useState("all");
  const [type, setType] = useState("all");
  const [query, setQuery] = useState("");
  const [editing, setEditing] = useState(null); // the issue being edited, or null
  const [editForm, setEditForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [newIssueOpen, setNewIssueOpen] = useState(false);

  const isAdmin = currentUser?.role === "admin";
  // "owner" has identical permissions to "manager" everywhere — see
  // is_manager_or_admin() in schema_owner_role.sql.
  const isManager = currentUser?.role === "manager" || currentUser?.role === "owner";

  const load = async () => {
    const [i, ini, p, t, u, tm] = await Promise.all([
      api.get("/issues"),
      api.get("/initiatives").catch(() => ({ data: [] })),
      api.get("/projects").catch(() => ({ data: [] })),
      api.get("/tasks").catch(() => ({ data: [] })),
      api.get("/users"),
      api.get("/team/members").catch(() => null),
    ]);
    setIssues(i.data || []);
    setInitiatives(ini.data || []);
    setProjects(p.data || []);
    setTasks(t.data || []);
    setUsers(sortByName(u.data || []));
    // People in the caller's initiatives — who an issue can be assigned to.
    setTeamUsers(sortByName((tm ? tm.data : u.data) || []));
  };
  useEffect(() => { load(); }, []);

  const iMap = Object.fromEntries(initiatives.map((x) => [x._id, x]));
  const pMap = Object.fromEntries(projects.map((x) => [x._id, x]));
  const tMap = Object.fromEntries(tasks.map((x) => [x._id, x]));
  const uMap = Object.fromEntries(users.map((x) => [x._id, x.name || x.email]));

  // Walks an issue up to whichever level it was reported at, resolving the
  // rest of the chain (task -> project -> initiative) from the ids already
  // in scope — same "resolve via already-fetched lists" approach the
  // Projects/Tasks grids use for their Initiative column, rather than
  // storing redundant parent ids on the issue itself.
  const entityChain = (iss) => {
    if (iss.task_id) {
      const task = tMap[iss.task_id];
      const project = task ? pMap[task.project_id] : null;
      const initiative = project ? iMap[project.initiative_id] : null;
      return { level: "Task", label: task?.title || "Unknown task", to: `/tasks/${iss.task_id}`, project, initiative };
    }
    if (iss.project_id) {
      const project = pMap[iss.project_id];
      const initiative = project ? iMap[project.initiative_id] : null;
      return { level: "Project", label: project?.title || "Unknown project", to: `/projects/${iss.project_id}`, project, initiative };
    }
    const initiative = iMap[iss.initiative_id];
    return { level: "Initiative", label: initiative?.title || "Unknown initiative", to: `/initiatives/${iss.initiative_id}`, project: null, initiative };
  };

  const canEditIssue = (iss) => isAdmin || isManager || iss.raised_by === currentUser?.id || iss.assigned_to === currentUser?.id;

  // Everything except the level tiles themselves — same "count over every
  // OTHER filter" rule the Tasks page's metric tiles follow, so picking
  // Initiative/Type/Status narrows the tile counts instead of the tiles
  // narrowing each other.
  const levelScoped = issues
    .filter((i) => projectFilter === "all" || entityChain(i).project?._id === projectFilter)
    .filter((i) => status === "all" || i.status === status)
    .filter((i) => type === "all" || i.type === type)
    .filter((i) => {
      const chain = entityChain(i);
      return matchesQuery(
        query, i.issue_number, i.description, i.root_cause, i.comment,
        chain.label, chain.project?.title, chain.initiative?.title,
      );
    });

  const levelCounts = { all: levelScoped.length };
  ["project", "task"].forEach((lv) => {
    levelCounts[lv] = levelScoped.filter((i) => entityChain(i).level.toLowerCase() === lv).length;
  });

  const filtered = levelScoped.filter((i) => level === "all" || entityChain(i).level.toLowerCase() === level);

  const updateStatus = async (iss, s) => {
    if (iss.status === s) return;
    if (s === "closed" && !iss.root_cause?.trim()) {
      toast.error("Add a root cause before closing this issue");
      return;
    }
    const previous = issues;
    setIssues((cur) => cur.map((x) => (x._id === iss._id ? { ...x, status: s } : x)));
    try {
      await api.patch(`/issues/${iss._id}`, { status: s });
    } catch (e) {
      setIssues(previous);
      toast.error(formatApiErrorDetail(e));
    }
  };

  const openEdit = (iss) => {
    setEditing(iss);
    setEditForm({
      status: iss.status,
      assigned_to: iss.assigned_to || "",
      eta: toDateInput(iss.eta),
      revised_eta: toDateInput(iss.revised_eta),
      root_cause: iss.root_cause || "",
      comment: iss.comment || "",
      attachment_url: iss.attachment_url || "",
    });
  };

  const saveEdit = async () => {
    if (!editForm.assigned_to) {
      toast.error("Assign this issue to someone");
      return;
    }
    if (!editForm.eta) {
      toast.error("Set an ETA");
      return;
    }
    if (editForm.status === "closed" && !editForm.root_cause.trim()) {
      toast.error("Add a root cause before closing this issue");
      return;
    }
    setSaving(true);
    try {
      await api.patch(`/issues/${editing._id}`, {
        status: editForm.status,
        assigned_to: editForm.assigned_to || null,
        eta: editForm.eta || null,
        revised_eta: editForm.revised_eta || null,
        root_cause: editForm.root_cause || null,
        comment: editForm.comment || null,
        attachment_url: editForm.attachment_url || null,
      });
      toast.success("Issue updated");
      setEditing(null);
      setEditForm(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e));
    } finally {
      setSaving(false);
    }
  };

  // Both filters always list every fixed value, not just ones already in
  // use on an existing issue — so a status/type nobody has picked yet is
  // still selectable rather than only appearing once one exists.
  const statusOptions = ISSUE_STATUSES;
  const typeOptions = ISSUE_TYPES;

  const renderCell = (key, iss) => {
    switch (key) {
      case "number":
        return <span className="font-mono text-xs text-zinc-600 tabular-nums">{iss.issue_number || "—"}</span>;
      case "description":
        return <div className="break-words" title={iss.description}>{iss.description}</div>;
      case "entity": {
        const chain = entityChain(iss);
        return (
          <div className="min-w-0">
            <Link to={chain.to} className="block break-words font-medium hover:text-[#0176D3]" title={chain.label}>
              {chain.label}
            </Link>
            <div className="trk-uppercase-label mt-0.5">{chain.level}</div>
          </div>
        );
      }
      case "type":
        return (
          <span className="inline-flex items-center px-1.5 py-0.5 text-[10px] font-mono uppercase tracking-wider rounded-sm border bg-white text-zinc-600 border-zinc-300">
            {ISSUE_TYPE_LABELS[iss.type] || iss.type}
          </span>
        );
      case "status":
        return (
          <StatusSelectPill
            status={iss.status}
            statuses={ISSUE_STATUSES}
            disabled={!canEditIssue(iss)}
            onChange={(v) => updateStatus(iss, v)}
            testid={`issue-status-${iss._id}`}
          />
        );
      case "raised_by":
        return <span className="text-zinc-600" title={uMap[iss.raised_by] || ""}>{uMap[iss.raised_by] || "—"}</span>;
      case "assigned_to":
        return <span className="text-zinc-600" title={uMap[iss.assigned_to] || ""}>{uMap[iss.assigned_to] || "—"}</span>;
      case "reported_date":
        return <span className="font-mono text-xs text-zinc-600">{fmtDate(iss.reported_date)}</span>;
      case "eta":
        return <span className="font-mono text-xs text-zinc-600">{fmtDate(iss.eta)}</span>;
      case "root_cause":
        return <div className="break-words text-zinc-600" title={iss.root_cause || ""}>{iss.root_cause || "—"}</div>;
      case "comment":
        return <div className="break-words text-zinc-600" title={iss.comment || ""}>{iss.comment || "—"}</div>;
      default:
        return null;
    }
  };

  return (
    <AppLayout>
      <div className="sticky top-0 z-20 flex flex-wrap items-center justify-between gap-3 px-4 py-4 trk-sticky-header md:px-8">
        <div>
          <div className="trk-uppercase-label">Execution</div>
          <h1 className="text-3xl font-extrabold tracking-tight font-display">Issues</h1>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Select value={projectFilter} onValueChange={setProjectFilter}>
            <SelectTrigger className="w-full rounded-sm sm:w-44" data-testid="issue-filter-project"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All projects</SelectItem>
              {sortByTitle(projects).map((p) => <SelectItem key={p._id} value={p._id}>{p.title}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={type} onValueChange={setType}>
            <SelectTrigger className="w-full rounded-sm sm:w-44" data-testid="issue-filter-type"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All types</SelectItem>
              {sortByLabel(typeOptions, (t) => ISSUE_TYPE_LABELS[t] || t).map((t) => <SelectItem key={t} value={t}>{ISSUE_TYPE_LABELS[t] || t}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="w-full rounded-sm sm:w-40" data-testid="issue-filter-status"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {sortByLabel(statusOptions, (s) => STATUS_STYLES[s]?.label || s).map((s) => <SelectItem key={s} value={s}>{STATUS_STYLES[s]?.label || s}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button onClick={() => setNewIssueOpen(true)} data-testid="new-issue-button" className="rounded-sm bg-[#0176D3] hover:bg-[#005FB2]">
            <PlusIcon size={14} weight="bold" className="mr-1" /> New Issue
          </Button>
        </div>
      </div>

      <div className="px-4 py-6 space-y-6 md:px-8 trk-fade-in">
        {/* Segregation by level — which kind of thing an issue was reported
            against. Doubles as the level filter, same convention as the
            Tasks page's status tiles: click a tile to narrow the grid below,
            click it again (or "All") to clear it. */}
        <div className="grid grid-cols-3 overflow-hidden bg-white border rounded-sm border-border">
          {LEVELS.map((lv, idx) => (
            <button
              key={lv.id}
              onClick={() => setLevel(lv.id)}
              data-testid={`issue-level-${lv.id}`}
              className={`p-4 text-left border-border transition-colors ${idx < LEVELS.length - 1 ? "border-r" : ""} ${level === lv.id ? "bg-[#EFF6FF]" : "hover:bg-zinc-50"}`}
            >
              <div className="trk-uppercase-label text-zinc-500">{lv.label}</div>
              <div className="mt-1 text-3xl font-extrabold font-display tabular-nums">{levelCounts[lv.id]}</div>
            </button>
          ))}
        </div>

        <DataTable
          toolbarStart={
            <TableSearch
              value={query}
              onChange={setQuery}
              placeholder="Search issues…"
              label="Search issues"
              testid="issues-search"
            />
          }
          storageKey={COL_STORAGE_KEY}
          testIdPrefix="issues"
          columns={COLUMN_DEFS}
          rows={filtered}
          rowKey={(i) => i._id}
          onRowClick={(iss) => canEditIssue(iss) && openEdit(iss)}
          pageSize={20}
          emptyTitle="No issues"
          emptyMessage="Nothing has been reported yet — use New Issue above, or the warning icon on a project or task, to report one."
          cellFor={renderCell}
        />
      </div>

      <Dialog open={!!editing} onOpenChange={(v) => { if (!v) { setEditing(null); setEditForm(null); } }}>
        <DialogContent className="max-w-lg rounded-sm">
          <DialogHeader>
            <DialogTitle className="font-display">
              {editing?.issue_number} — {entityChain(editing || {}).label}
            </DialogTitle>
          </DialogHeader>
          {editForm && (
            <div className="space-y-4">
              <div className="text-sm break-words text-zinc-600">{editing.description}</div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="block mb-2 trk-uppercase-label">Status <span className="text-red-500">*</span></Label>
                  <Select value={editForm.status} onValueChange={(v) => setEditForm({ ...editForm, status: v })}>
                    <SelectTrigger className="rounded-sm" data-testid="issue-edit-status"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {sortByLabel(ISSUE_STATUSES, (s) => STATUS_STYLES[s]?.label || s).map((s) => <SelectItem key={s} value={s}>{STATUS_STYLES[s]?.label || s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="block mb-2 trk-uppercase-label">Assigned <span className="text-red-500">*</span></Label>
                  <Select value={editForm.assigned_to} onValueChange={(v) => setEditForm({ ...editForm, assigned_to: v })}>
                    <SelectTrigger className="rounded-sm" data-testid="issue-edit-assigned"><SelectValue placeholder="Select…" /></SelectTrigger>
                    <SelectContent>
                      {/* People in my initiatives, plus whoever is already assigned (even if outside them). */}
                      {[
                        ...teamUsers,
                        ...users.filter((u) => u._id === editForm.assigned_to && !teamUsers.some((t) => t._id === u._id)),
                      ].map((u) => <SelectItem key={u._id} value={u._id}>{u.name || u.email}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="block mb-2 trk-uppercase-label">ETA <span className="text-red-500">*</span></Label>
                  <Input type="date" value={editForm.eta} onChange={(e) => setEditForm({ ...editForm, eta: e.target.value })} className="rounded-sm" data-testid="issue-edit-eta" />
                </div>
                <div>
                  <Label className="block mb-2 trk-uppercase-label">Revised ETA</Label>
                  <Input type="date" value={editForm.revised_eta} onChange={(e) => setEditForm({ ...editForm, revised_eta: e.target.value })} className="rounded-sm" data-testid="issue-edit-revised-eta" />
                </div>
              </div>
              <div>
                <Label className="block mb-2 trk-uppercase-label">Comment</Label>
                <Textarea value={editForm.comment} onChange={(e) => setEditForm({ ...editForm, comment: e.target.value })} className="rounded-sm" rows={2} maxLength={ISSUE_TEXT_MAX_LENGTH} data-testid="issue-edit-comment" />
              </div>
              <div>
                <Label className="block mb-2 trk-uppercase-label">
                  Root Cause{editForm.status === "closed" ? <span className="text-red-500"> *</span> : ""}
                </Label>
                <Textarea value={editForm.root_cause} onChange={(e) => setEditForm({ ...editForm, root_cause: e.target.value })} className="rounded-sm" rows={2} maxLength={ISSUE_TEXT_MAX_LENGTH} data-testid="issue-edit-root-cause" />
              </div>
              <div>
                <Label className="block mb-2 trk-uppercase-label">Attachment URL</Label>
                <Input value={editForm.attachment_url} onChange={(e) => setEditForm({ ...editForm, attachment_url: e.target.value })} className="rounded-sm" data-testid="issue-edit-attachment" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button onClick={saveEdit} disabled={saving} data-testid="issue-edit-save" className="rounded-sm bg-[#0176D3] hover:bg-[#005FB2]">
              {saving ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ReportIssueModal
        open={newIssueOpen}
        onOpenChange={setNewIssueOpen}
        projectOptions={sortByTitle(projects)}
        taskOptions={sortByTitle(tasks)}
        onCreated={load}
      />
    </AppLayout>
  );
}
